import type { CostCoverage } from '../../../components/CostCoverageNote';
import { api } from '../../../lib/api';

export interface PersonaInfo {
  key: 'anna' | 'jan' | null;
  name: string | null;
  voiceId: string;
}

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  weekDays: { date: string; completed: boolean; isOffDay: boolean }[];
}

export interface LessonsSummary {
  completed: number;
  total: number;
  inProgress: number;
  avgSelfTestScore: number | null;
  totalTimeSpentSeconds: number;
  avgAudioListenedPct: number | null;
}

export interface QuizSummary {
  total: number;
  correct: number;
  correctRate: number;
  avgTimeSeconds: number | null;
}

export interface SessionsSummary {
  total: number;
  totalElapsedSeconds: number;
  avgElapsedSeconds: number;
  overtimeCount: number;
}

export interface DailyHeatmapEntry {
  date: string;
  completedMinutes: number;
  plannedMinutes: number;
  completionRatio: number;
  streakDayNumber: number;
  hadPowerSession: boolean;
  isLearningDay: boolean;
}

export interface TopicMasteryItem {
  topicId: string;
  topicTitle: string;
  iconName: string;
  category: string;
  masteryPercent: number;
  lessonsCompleted: number;
  lessonsTotal: number;
  avgQuizScore: number;
  flashcardProgress: number;
  color?: string;
}

export interface WeeklyScoreEntry {
  week: string;
  avgScore: number;
  quizCount: number;
}

export type TutorUsageSource =
  | 'tutor'
  | 'voiceTutor'
  | 'oralExam'
  | 'scoring'
  | 'aiQuiz'
  | 'flashcard'
  | string;

export interface TutorUsageBySource {
  coverage?: CostCoverage; amountExact?: string | null;
  source: TutorUsageSource;
  callCount: number;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadInputTokens: number | null;
  cacheCreationInputTokens: number | null;
  totalTokens: number | null;
  costUsd: number | null;
  costEur: number | null;
}

export interface TutorUsageDaily {
  date: string;
  totalTokens: number | null;
  costUsd: number | null;
  costEur: number | null;
}

export interface TutorUsageBreakdown {
  totals: {
    coverage?: CostCoverage; amountExact?: string | null;
    callCount: number;
    totalTokens: number | null;
    costUsd: number | null;
    costEur: number | null;
  };
  bySource: TutorUsageBySource[];
  daily: TutorUsageDaily[];
}

export interface VoiceSessionsSummary {
  count: number;
  totalDurationSec: number;
  totalCostCents: number;
  totalCostEur: number;
  last30DaysCount: number;
}

export interface UserLearningData {
  hero: {
    lastActiveAt: string | null;
    persona: PersonaInfo;
    streak: StreakData;
  };
  lessons: LessonsSummary;
  quiz: QuizSummary;
  sessions: SessionsSummary;
  daily: DailyHeatmapEntry[];
  topicMastery: TopicMasteryItem[];
  weeklyQuizScores: WeeklyScoreEntry[];
  tutorUsage: TutorUsageBreakdown;
  voiceSessions: VoiceSessionsSummary;
  achievements: {
    unlocked: { slug: string; unlockedAt: string }[];
    milestones: { topicId: string; topicTitle: string; achievedAt: string }[];
  };
  meta: {
    heatmapDays: number;
    tutorDailyDays: number;
    weeklyScoreWeeks: number;
    usdToEur: number;
  };
}

export function fetchUserLearning(id: string, app: string): Promise<UserLearningData> {
  return api.get<UserLearningData>(`/admin/users/${id}/learning?app=${encodeURIComponent(app)}`);
}

// ── Tutor-Chats (Spec tutor-text-session-sync.md B3) ───────────────

export interface TutorSessionListItem {
  id: string;
  title: string | null;
  contextType: string;
  contextId: string | null;
  lastUserMessageAt: string | null;
  messageCount: number;
}

export interface TutorTranscriptMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface TutorSessionTranscript {
  id: string;
  title: string | null;
  contextType: string;
  contextId: string | null;
  messages: TutorTranscriptMessage[];
}

export function fetchUserTutorSessions(
  id: string,
  app: string,
): Promise<{ sessions: TutorSessionListItem[] }> {
  return api.get<{ sessions: TutorSessionListItem[] }>(
    `/admin/users/${id}/tutor-sessions?app=${encodeURIComponent(app)}`,
  );
}

export function fetchUserTutorSessionTranscript(
  id: string,
  sessionId: string,
): Promise<TutorSessionTranscript> {
  return api.get<TutorSessionTranscript>(`/admin/users/${id}/tutor-sessions/${sessionId}`);
}

// ── Tutor-Memory (Spec tutor-text-coverage.md C5) ──────────────────

export interface TutorMemoryData {
  profile: {
    domainKnowledge: Record<string, string>;
    context: string[];
    preferences: {
      answerLength: string;
      tone: string;
      likesAnalogies: boolean;
      likesRoleplay: boolean;
      likesQuizDrills: boolean;
    };
    backgroundNotes: string[];
  };
  learningState: {
    phase: string;
    recurringMisconceptions: Array<{
      id?: string;
      topic: string;
      misconception: string;
      correction: string;
      observedCount: number;
      lastObservedAt: string;
    }>;
    recentTopics: Array<{
      topicSlug: string;
      topicTitle?: string;
      lastDiscussedAt: string;
      verdict: 'solid' | 'shaky' | 'unfinished';
    }>;
    openThreads: Array<{ note: string; mentionedAt: string }>;
    recentDrills: Array<{ kind: string; fingerprint: string; askedAt: string }>;
  };
  notes: Array<{
    id: string;
    text: string;
    importance: 1 | 2 | 3;
    pinned: boolean;
    createdAt: string;
  }>;
}

export interface TutorMemoryResponse {
  memory: TutorMemoryData | null;
  meta: {
    version: number;
    lastUpdatedBy: string | null;
    lastUpdatedAt: string;
    resetAt: string | null;
  } | null;
}

export function fetchUserTutorMemory(id: string, app: string): Promise<TutorMemoryResponse> {
  return api.get<TutorMemoryResponse>(
    `/admin/users/${id}/tutor-memory?app=${encodeURIComponent(app)}`,
  );
}
